import { Gift, Sparkles, Star, ShoppingBag, Loader2 } from "lucide-react";
import { useCheckout } from "@/lib/useCheckout";

const combos = [
  { id: "combo-1", name: "Combo 1", contents: "Small Planter · 4 oz Candle · Hair Growth Oil (2 oz)", price: 60, tone: "from-[#F3E3DF] to-[#F8ECD9]", accent: "#B7574E", image: "/assets/combos/combo_1.png" },
  { id: "combo-2", name: "Combo 2", contents: "Medium Planter · 8 oz Candle · Hair Growth Oil (2 oz)", price: 85, tone: "from-[#E6D7E3] to-[#F4EFE6]", accent: "#7E5C8E", featured: true, image: "/assets/combos/combo_2.png" },
  { id: "combo-3", name: "Combo 3", contents: "Large Planter · 12 oz Candle · Hair Growth Oil (2 oz)", price: 120, tone: "from-[#F0E9D6] to-[#F4EFE6]", accent: "#5D2138", featured: true, image: "/assets/combos/combo_3.png" },
  { id: "combo-4", name: "Combo 4", contents: "Small + Medium Planters · 8 oz Candle · Hair Growth Oil (2 oz)", price: 115, tone: "from-[#E8EEDA] to-[#F1F5E4]", accent: "#2C402B", image: "/assets/combos/combo_4.png" },
  { id: "combo-5", name: "Combo 5", contents: "Large + Medium Planters · 12 oz Candle · Hair Growth Oil (2 oz)", price: 165, tone: "from-[#F3E3DF] to-[#F0E9D6]", accent: "#B7574E", image: "/assets/combos/combo_5.png" },
  { id: "combo-6", name: "Combo 6", contents: "Small + Medium + Large Planters · Two 12 oz Candles · Hair Growth Oil (2 oz) · 414 LLC T-Shirt", price: 235, tone: "from-[#F0E9D6] to-[#E6D7E3]", accent: "#5D2138", featured: true, image: "/assets/combos/combo_6.png" },
];

export default function CombosSection() {
  const { buy, loadingId } = useCheckout();

  return (
    <section
      data-testid="combos-section"
      id="combos"
      className="relative bg-brand-cream py-24 lg:py-32 overflow-hidden"
    >
      <img
        src="/assets/flower_bouquet.png"
        alt=""
        aria-hidden="true"
        className="absolute -top-6 left-1/2 -translate-x-1/2 w-60 lg:w-80 opacity-85 pointer-events-none hidden md:block"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 z-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block bg-brand-moss text-brand-gold px-6 py-1.5 text-xs uppercase tracking-[0.35em] -rotate-1">
            Beauty &amp; Wellness Combos
          </span>
          <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
            Six carefully paired collections.
            <br />
            <span className="italic text-[#B7574E]">Ready to gift, ready to bloom.</span>
          </h2>
          <p className="mt-5 text-lg text-brand-ink/75">
            Each combo is bundled at a member-friendly rate. Qualifying orders over $100 receive a{" "}
            <span className="font-semibold text-brand-moss">complimentary hand-poured candle.</span>
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {combos.map((c) => (
            <article
              key={c.id}
              data-testid={c.id}
              className={`relative bg-gradient-to-br ${c.tone} rounded-3xl overflow-hidden border border-brand-moss/10 shadow-sm hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500 flex flex-col`}
            >
              <div className="relative w-full aspect-[4/3] overflow-hidden bg-brand-cream/40">
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                />
                {c.featured && (
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-brand-moss text-brand-gold text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full shadow-md">
                    <Star className="w-3 h-3 fill-current" />
                    Popular
                  </span>
                )}
                <span
                  className="absolute bottom-4 left-4 inline-flex items-center justify-center w-11 h-11 rounded-full text-brand-cream shadow-lg"
                  style={{ backgroundColor: c.accent }}
                >
                  <Gift className="w-5 h-5" strokeWidth={1.6} />
                </span>
              </div>

              <div className="p-7 flex-1 flex flex-col">
                <h3 className="font-serif text-2xl text-brand-moss leading-tight">
                  {c.name}
                </h3>
                <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed min-h-[3.5rem]">
                  {c.contents}
                </p>
                <div className="mt-6 flex items-end justify-between">
                  <span className="font-serif text-3xl text-brand-moss">${c.price}</span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-brand-moss/60">
                    Handcrafted with prayer
                  </span>
                </div>

                <button
                  type="button"
                  data-testid={`buy-${c.id}-btn`}
                  onClick={() => buy(c.id, c.name)}
                  disabled={loadingId === c.id}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-moss text-brand-gold px-6 py-3 text-xs uppercase tracking-[0.3em] rounded-full hover:bg-brand-moss/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
                >
                  {loadingId === c.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Opening…
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" strokeWidth={1.8} />
                      Buy Combo
                    </>
                  )}
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 max-w-3xl mx-auto rounded-3xl bg-brand-moss text-brand-cream px-8 py-8 lg:px-12 lg:py-10 flex flex-col md:flex-row items-center gap-6 shadow-xl">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-brand-gold text-brand-moss flex-shrink-0">
            <Sparkles className="w-7 h-7" strokeWidth={1.5} />
          </span>
          <div className="text-center md:text-left flex-1">
            <p className="font-serif italic text-2xl text-brand-cream leading-snug">
              FREE candle with any qualifying order of $100 or more.
            </p>
            <p className="mt-1 text-sm text-brand-cream/75">
              Members are auto-credited a complimentary hand-poured candle at every $100 milestone —
              no promo code needed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
